import { Button, DatePicker, Input, Radio, Select, Skeleton, Space, Drawer } from 'antd';
import dayjs from 'dayjs';
import React, { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  RemoteImageAlert_GetList_Request,
  RemoteImageAlert_GetList_ResponseItem,
  remoteRemoteImageAlert_GetLandsatList,
} from '~/common/typescript/apis/RemoteImageAlertAPIs';
import LandsatItem from './LandsatItem';
import FormItemWrapper from '~/components/common/FormItemWrapper/FormItemWrapper';
import { useAtom, useSetAtom } from 'jotai';
import { satelliteRecordCountAtom, satelliteFilterTriggerAtom, satelliteSearchPointAtom } from '../../store';

enum FilterBy {
  NONE = 0,
  POINT_FROM_LAT_LON = 1,
  POINT_FROM_QGIS = 2,
}

const LandsatFinder: React.FC = () => {
  const childRefs = useRef([]);
  const [qgisCoordinateText, setQgisCoordinateText] =
    React.useState<string>('');
  const [fromDateString, setFromDate] = React.useState<string>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [toDateString, setToDate] = React.useState<string>(
    dayjs().format('YYYY-MM-DD'),
  );
  const [filterBy, setFilterBy] = React.useState<FilterBy>(FilterBy.NONE);
  const [latitude, setLatitude] = React.useState<number>(21.027849422068357);
  const [longitude, setLongitude] = React.useState<number>(105.85229222989688);
  const [isLoading, setLoading] = useState(false);
  const [cloudCover, setCloudCover] = useState(1.0);

  const [remoteImageList, setRemoteImageList] = React.useState<
    RemoteImageAlert_GetList_ResponseItem[]
  >([]);

  const [filterVisible, setFilterVisible] = useState(false);
  const [satelliteSearchPoint] = useAtom(satelliteSearchPointAtom);
  const lastProcessedPointRef = useRef<number | null>(null);

  const executeSearch = async (overrideCoords?: [number, number], overrideFilterBy?: FilterBy) => {
    try {
      setLoading(true);
      const activeFilter = overrideFilterBy !== undefined ? overrideFilterBy : filterBy;
      const coords = overrideCoords ?? [longitude, latitude];
      const request: RemoteImageAlert_GetList_Request = {
        start_date: dayjs(fromDateString),
        end_date: dayjs(toDateString),
        cloud_cover: 100 * cloudCover,
      };
      if (
        activeFilter === FilterBy.POINT_FROM_LAT_LON ||
        activeFilter === FilterBy.POINT_FROM_QGIS
      ) {
        request.point = {
          type: 'Point',
          coordinates: coords,
        };
      }
      const res = await remoteRemoteImageAlert_GetLandsatList(request);
      setRemoteImageList(res);
      setFilterVisible(false); // Close the filter after search
    } catch (error) {
      console.error('Error searching Landsat imagery:', error);
    } finally {
      setLoading(false);
    }
  };

  const onClick_Search = async () => {
    await executeSearch();
  };

  useEffect(() => {
    if (filterBy === FilterBy.POINT_FROM_QGIS) {
      const [lat, lon] = qgisCoordinateText.split(',').map((s) => Number(s));
      if (!isNaN(lat) && !isNaN(lon)) {
        setLatitude(lat);
        setLongitude(lon);
      }
    }
  }, [qgisCoordinateText, filterBy]);

  const setRecordCount = useSetAtom(satelliteRecordCountAtom);
  const setFilterTrigger = useSetAtom(satelliteFilterTriggerAtom);

  // Update record count when the list changes
  useEffect(() => {
    setRecordCount(remoteImageList.length);
  }, [remoteImageList, setRecordCount]);

  // Register the filter trigger function
  useEffect(() => {
    setFilterTrigger(() => {
      setFilterVisible(true);
    });
    return () => {
      setFilterTrigger(null);
    };
  }, [setFilterTrigger]);

  // React to search point from context menu
  useEffect(() => {
    if (satelliteSearchPoint && satelliteSearchPoint.timestamp && satelliteSearchPoint.timestamp !== lastProcessedPointRef.current) {
      lastProcessedPointRef.current = satelliteSearchPoint.timestamp;
      setLatitude(satelliteSearchPoint.lat);
      setLongitude(satelliteSearchPoint.lng);
      setFilterBy(FilterBy.POINT_FROM_LAT_LON);
      executeSearch([satelliteSearchPoint.lng, satelliteSearchPoint.lat], FilterBy.POINT_FROM_LAT_LON);
    }
  }, [satelliteSearchPoint]);

  // Auto-fetch data on mount if no search point is being processed
  useEffect(() => {
    if (satelliteSearchPoint && satelliteSearchPoint.timestamp) {
      // Handled by the search point effect above
      return;
    }
    executeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <UI_Wrapper>
        <UI_Container>
          
          {isLoading && <Skeleton />}
          {!isLoading && (
            <>
              <UI_ImageList_Title_Wrapper>
                <h2>
                  Kết quả tìm kiếm ({remoteImageList.length})
                  {filterBy === FilterBy.POINT_FROM_LAT_LON && (
                    <span style={{ fontSize: 13, fontWeight: 'normal', color: '#666', marginLeft: 8 }}>
                      ({longitude.toFixed(4)}, {latitude.toFixed(4)})
                    </span>
                  )}
                  {filterBy === FilterBy.NONE && (
                    <span style={{ fontSize: 13, fontWeight: 'normal', color: '#666', marginLeft: 8 }}>
                      (Tất cả khu vực)
                    </span>
                  )}
                </h2>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      // Skip the QGIS functionality as it's not relevant for the left/right map features
                    }}
                  >
                    Thêm tất cả ảnh lên QGIS
                  </Button>
                </Space>
              </UI_ImageList_Title_Wrapper>
              <UI_Item_List>
                {remoteImageList.map((remoteImage, index) => {
                  return (
                    <LandsatItem
                      item={remoteImage}
                      key={index}
                      ref={(el: any) => (childRefs.current[index] = el)}
                    />
                  );
                })}
              </UI_Item_List>
            </>
          )}
        </UI_Container>
      </UI_Wrapper>

      {/* Filter popup */}
      <Drawer
        title="Tìm kiếm ảnh vệ tinh Landsat"
        placement="right"
        closable={true}
        onClose={() => setFilterVisible(false)}
        open={filterVisible}
        width={375} // Mobile width
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <FormItemWrapper label="Từ ngày">
            <DatePicker
              defaultValue={dayjs(fromDateString)}
              style={{ width: '100%' }}
              onChange={(date: any, dateString: string | string[]) => {
                setFromDate(dateString as string);
              }}
            />
          </FormItemWrapper>
          <FormItemWrapper label="Đến ngày">
            <DatePicker
              defaultValue={dayjs(toDateString)}
              style={{ width: '100%' }}
              onChange={(date: any, dateString: string | string[]) => {
                setToDate(dateString as string);
              }}
            />
          </FormItemWrapper>
          <FormItemWrapper label="Độ phủ mây">
            <Select
              defaultValue={cloudCover}
              style={{ width: '100%' }}
              onChange={(value) => {
                setCloudCover(value);
              }}
              options={[
                { value: 1.0, label: 'Không lọc (tất cả)' },
                { value: 0.95, label: 'Nhỏ hơn 95%' },
                { value: 0.9, label: 'Nhỏ hơn 90%' },
                { value: 0.7, label: 'Nhỏ hơn 70%' },
                { value: 0.5, label: 'Nhỏ hơn 50%' },
                { value: 0.3, label: 'Nhỏ hơn 30%' },
                { value: 0.1, label: 'Nhỏ hơn 10%' },
              ]}
            />
          </FormItemWrapper>

          <div>
            <FormItemWrapper label="Lọc khu vực">
              <Radio.Group
                value={filterBy}
                onChange={(e) => {
                  setFilterBy(e.target.value);
                }}
                options={[
                  { value: FilterBy.NONE, label: 'Không lọc' },
                  { value: FilterBy.POINT_FROM_LAT_LON, label: 'Tọa độ điểm' },
                  { value: FilterBy.POINT_FROM_QGIS, label: 'Điểm từ QGIS' },
                ]}
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              />
            </FormItemWrapper>

            {filterBy === FilterBy.NONE && (
              <div style={{ marginBottom: 12, color: '#888', fontSize: 13 }}>
                Tìm kiếm toàn bộ ảnh Landsat mới nhất không giới hạn theo vị trí tọa độ.
              </div>
            )}

            {filterBy === FilterBy.POINT_FROM_QGIS && (
              <div style={{ marginBottom: 12 }}>
                <span
                  style={{
                    fontSize: 13,
                    color: '#666',
                    marginBottom: 6,
                    display: 'block',
                  }}
                >
                  Chuột phải tại QGIS / Copy Coordinates / EPSG:4326 - WGS84
                </span>
                <Input
                  placeholder="WGS84 lat, lon"
                  value={qgisCoordinateText}
                  onChange={(event) => {
                    setQgisCoordinateText(event.target.value);
                  }}
                />
              </div>
            )}

            {filterBy === FilterBy.POINT_FROM_LAT_LON && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                <div>
                  <span style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'block' }}>Kinh độ (Longitude):</span>
                  <Input
                    placeholder="Kinh độ"
                    value={longitude}
                    type="number"
                    step="0.0001"
                    onChange={(event) => {
                      setLongitude(Number(event.target.value));
                    }}
                  />
                </div>
                <div>
                  <span style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'block' }}>Vĩ độ (Latitude):</span>
                  <Input
                    placeholder="Vĩ độ"
                    value={latitude}
                    type="number"
                    step="0.0001"
                    onChange={(event) => {
                      setLatitude(Number(event.target.value));
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <Button
            type="primary"
            onClick={onClick_Search}
          >
            Tìm kiếm
          </Button>
        </div>
      </Drawer>
    </>
  );
};
export default memo(LandsatFinder);

const UI_Wrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
`;

const UI_Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
`;

const UI_ImageList_Title_Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const UI_Item_List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;
